import { CREATE_OUTREACH_PROGRAMME } from "@lib/graphql/outreach";
import { useState } from "react";
import { useMutation } from "urql";

interface Props {
  onCreated?: () => void;
  onCancel: () => void;
}

export function NewProgrammeForm({ onCreated, onCancel }: Props) {
  const [, createProgramme] = useMutation(CREATE_OUTREACH_PROGRAMME);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [toneGuidance, setToneGuidance] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setBusy(true);
    setErrors([]);
    const result = await createProgramme({
      input: { name: name.trim(), description, toneGuidance },
    });
    const payload = result.data?.createOutreachProgramme;
    if (payload && payload.errors.length === 0) {
      onCreated?.();
    } else {
      setErrors(payload?.errors ?? ["Could not create programme"]);
    }
    setBusy(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
    >
      <div>
        <label className="block text-xs font-medium text-text-secondary">Name *</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary">Description</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-text-secondary">Tone guidance</label>
        <textarea
          value={toneGuidance}
          onChange={(e) => setToneGuidance(e.target.value)}
          rows={3}
          placeholder="Formal, two-sentence connect notes; lead with the concrete signal"
          className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm"
        />
      </div>
      {errors.length > 0 && (
        <ul className="text-xs text-red-600 dark:text-red-400">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy || !name.trim()}
          className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          Create programme
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-input-border px-3 py-1.5 text-xs text-text-secondary hover:bg-card-hover"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
